import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, Teleport as _Teleport, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = { class: "chart-base" }
const _hoisted_2 = {
  id: "chart-tooltip",
  style: {"display":"none"},
  class: "chart-tooltip container"
}
const _hoisted_3 = { class: "content" }
const _hoisted_4 = {
  id: "row-x",
  class: "row row-x"
}
const _hoisted_5 = { class: "col" }
const _hoisted_6 = {
  id: "row-y",
  class: "row-y"
}
const _hoisted_7 = { class: "col" }
const _hoisted_8 = { class: "row flex-1" }
const _hoisted_9 = { class: "row flex-1" }
const _hoisted_10 = ["width", "height", "viewBox"]
const _hoisted_11 = ["width", "height", "transform", "data-padding"]

import { computed, ref, toRefs, watch } from 'vue'
import { ChartComponent } from '.'
import { componentMap } from './components'
import md5 from 'md5'
import TextBase from '@/design-system/atoms/TextBase.vue'
import { getTextWidth } from '@/utils/text-width'


export default /*@__PURE__*/_defineComponent({
  __name: 'ChartBase',
  props: {
    height: { default: '100%' },
    components: {},
    paddingOverride: { default: () => ({}) }
  },
  setup(__props: any) {

const props = __props

const { height: containerHeight, components } = toRefs(props)

const svgRef = ref<SVGSVGElement | null>(null)
const groupRef = ref<SVGSVGElement | null>(null)
const padding = computed(() => {
  const leftAxis = props.components.find((c) => c.type === 'axis' && c.position === 'left') as
    | ChartComponent<'axis'>
    | undefined
  const bottomAxis = props.components.find((c) => c.type === 'axis' && c.position === 'bottom') as
    | ChartComponent<'axis'>
    | undefined
  const maxLength =
    (leftAxis?.tickLabels ?? leftAxis?.ticks.map((t) => t.toString()))?.reduce(
      (a, b) => (a.length > b.length ? a : b),
      ''
    ) ?? ''
  const fontSize = leftAxis?.font?.size ?? 10
  const font = leftAxis?.font?.family ?? 'Inter'
  const textWidth = getTextWidth(maxLength, `${fontSize}px ${font}`)
  const leftPadding = textWidth + 14
  const bottomPadding = 30 + textWidth * Math.sin((bottomAxis?.rotate ?? 0) * (Math.PI / 180))
  return {
    top: 30,
    right: 20,
    bottom: bottomPadding,
    left: leftPadding,
    ...props.paddingOverride
  }
})

const viewWidth = ref<number | undefined>(undefined)
const viewHeight = ref<number | undefined>(undefined)

const width = computed(() =>
  viewWidth.value ? viewWidth.value - padding.value.right - padding.value.left : undefined
)
const height = computed(() =>
  viewHeight.value ? viewHeight.value - padding.value.top - padding.value.bottom : undefined
)

const viewBox = computed(() => `0 0 ${viewWidth.value ?? 0} ${viewHeight.value ?? 0}`)

const componentsWithId = computed(() =>
  components.value.map((c) => ({ ...c, id: md5(JSON.stringify(c)) }))
)

const computedHeight = computed(() => containerHeight?.value ?? '100%')

watch(svgRef, () => {
  if (!svgRef.value) return
  new ResizeObserver((entries) => {
    const { width, height } = entries[0].contentRect
    viewWidth.value = width
    viewHeight.value = height
  }).observe(svgRef.value)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(TextBase, {
                class: "info",
                text: "Info",
                color: "primary",
                weight: "bold",
                type: "tiny"
              })
            ])
          ]),
          _createElementVNode("div", _hoisted_6, [
            _cache[0] || (_cache[0] = _createElementVNode("div", { class: "col" }, [
              _createElementVNode("svg", {
                width: "3",
                height: "100%",
                "preserve-aspect-ratio": "none",
                "view-box": "0 0 3 24",
                class: "tooltip-legend"
              }, [
                _createElementVNode("g", null, [
                  _createElementVNode("rect", {
                    width: "100%",
                    height: "100%",
                    fill: "red",
                    class: "background-rect"
                  }),
                  _createElementVNode("rect", {
                    width: "100%",
                    height: "100%",
                    fill: "green",
                    class: "foreground-rect"
                  })
                ])
              ])
            ], -1)),
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("div", _hoisted_8, [
                _createVNode(TextBase, {
                  class: "label",
                  text: "Label",
                  color: "secondary",
                  weight: "bold",
                  type: "tiny"
                })
              ]),
              _createElementVNode("div", _hoisted_9, [
                _createVNode(TextBase, {
                  class: "value",
                  text: "-",
                  type: "label",
                  weight: "bold"
                })
              ])
            ])
          ])
        ])
      ])
    ])),
    (_openBlock(), _createElementBlock("svg", {
      ref_key: "svgRef",
      ref: svgRef,
      class: "chart",
      width: viewWidth.value,
      height: viewHeight.value,
      viewBox: viewBox.value
    }, [
      _cache[1] || (_cache[1] = _createElementVNode("defs", null, null, -1)),
      _createElementVNode("g", {
        ref_key: "groupRef",
        ref: groupRef,
        width: width.value,
        height: height.value,
        transform: `translate(${padding.value.left}, ${padding.value.top})`,
        "data-padding": JSON.stringify(padding.value)
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(componentsWithId.value, (component) => {
          return (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(componentMap)[component.type]), _mergeProps({
            key: component.id + computedHeight.value
          }, { ref_for: true }, component), null, 16))
        }), 128))
      ], 8, _hoisted_11)
    ], 8, _hoisted_10))
  ]))
}
}

})